import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggingService } from '../logging/logging.service';

/**
 * Service to detect app lifecycle events and determine if restoration is needed
 *
 * Native apps don't reload - they suspend and resume. This service makes the web app
 * behave the same way by:
 * 1. Detecting OS-forced kills (restore from IndexedDB)
 * 2. Preventing unnecessary manual refreshes
 * 3. Making restoration invisible when it does happen
 */
@Injectable({
  providedIn: 'root'
})
export class AppLifecycleService {
  private readonly STATE_KEY = 'app-lifecycle-state';
  private readonly HEARTBEAT_KEY = 'app-lifecycle-heartbeat';
  private readonly HEARTBEAT_INTERVAL = 5000; // 5 seconds

  private heartbeatTimer: any = null;

  // Observable for app backgrounded state
  private readonly isBackgroundedSubject = new BehaviorSubject<boolean>(false);
  public readonly isBackgrounded$: Observable<boolean> = this.isBackgroundedSubject.asObservable();

  // App states
  private readonly STATE = {
    ACTIVE: 'active',           // App is running
    BACKGROUNDED: 'backgrounded', // User backgrounded the app (visible=false)
    CLOSED_GRACEFULLY: 'closed',  // User closed tab/window normally
    KILLED: 'killed'             // OS killed the app (detected on next start)
  };

  constructor(private logger: LoggingService) {
    this.initializeLifecycleTracking();
  }

  /**
   * Initialize lifecycle tracking
   */
  private initializeLifecycleTracking(): void {
    // Set initial state
    this.setState(this.STATE.ACTIVE);

    // Start heartbeat to detect forced kills
    this.startHeartbeat();

    // Track visibility changes (backgrounding)
    this.setupVisibilityListener();

    // Track graceful closes
    this.setupBeforeUnloadListener();

    this.logger.info('AppLifecycleService', 'Lifecycle tracking initialized');
  }

  /**
   * Check if this app startup is recovering from an OS kill
   * Returns true ONLY if the app was backgrounded and never gracefully closed
   */
  public wasKilledByOS(): boolean {
    const lastState = localStorage.getItem(this.STATE_KEY);
    const lastHeartbeat = localStorage.getItem(this.HEARTBEAT_KEY);

    // If last state was backgrounded and heartbeat is old, OS likely killed us
    if (lastState === this.STATE.BACKGROUNDED) {
      if (lastHeartbeat) {
        const timeSinceHeartbeat = Date.now() - parseInt(lastHeartbeat, 10);

        // If heartbeat is more than 10 seconds old, assume OS kill
        if (timeSinceHeartbeat > 10000) {
          this.logger.info('AppLifecycleService',
            `Detected OS kill - last heartbeat was ${timeSinceHeartbeat}ms ago`);
          return true;
        }
      }

      // Backgrounded but recent heartbeat = manual refresh while backgrounded
      this.logger.info('AppLifecycleService',
        'App was backgrounded but heartbeat recent - likely manual refresh');
      return false;
    }

    // If last state was active/closed gracefully, this is a normal startup
    this.logger.info('AppLifecycleService',
      `Normal startup - last state: ${lastState || 'none'}`);
    return false;
  }

  /**
   * Check if this is the first time the app has ever run
   */
  public isFirstRun(): boolean {
    const lastState = localStorage.getItem(this.STATE_KEY);
    return lastState === null;
  }

  /**
   * Set the current app state
   */
  private setState(state: string): void {
    localStorage.setItem(this.STATE_KEY, state);
    this.logger.debug('AppLifecycleService', `State changed to: ${state}`);
  }

  /**
   * Start heartbeat to detect forced kills
   * Writes current timestamp every 5 seconds
   * If app is killed, heartbeat stops updating
   */
  private startHeartbeat(): void {
    // Write initial heartbeat
    this.updateHeartbeat();

    // Update heartbeat periodically
    this.heartbeatTimer = setInterval(() => {
      this.updateHeartbeat();
    }, this.HEARTBEAT_INTERVAL);

    this.logger.debug('AppLifecycleService', 'Heartbeat started');
  }

  /**
   * Update heartbeat timestamp
   */
  private updateHeartbeat(): void {
    localStorage.setItem(this.HEARTBEAT_KEY, Date.now().toString());
  }

  /**
   * Stop heartbeat (called on graceful close)
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Track when user backgrounds the app
   */
  private setupVisibilityListener(): void {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // User backgrounded the app
        this.setState(this.STATE.BACKGROUNDED);
        this.isBackgroundedSubject.next(true);
        this.logger.info('AppLifecycleService', 'App backgrounded');
      } else {
        // User foregrounded the app (no reload happened!)
        this.setState(this.STATE.ACTIVE);
        this.isBackgroundedSubject.next(false);
        this.logger.info('AppLifecycleService', 'App foregrounded (no reload)');
      }
    });
  }

  /**
   * Track graceful closes (user closes tab/window)
   */
  private setupBeforeUnloadListener(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('beforeunload', () => {
      // User is closing the tab/window
      this.setState(this.STATE.CLOSED_GRACEFULLY);
      this.stopHeartbeat();

      // FIX FIN-0046: Disconnect WebSocket on page navigation
      // ngOnDestroy() doesn't fire during page.goto() (E2E tests), so we need
      // to cleanup here. This fixes 85% E2E failure rate caused by WebSocket
      // connections staying alive after navigation, blocking domcontentloaded.
      if ((window as any).__finderwise_cleanup) {
        this.logger.info('AppLifecycleService', 'Calling WebSocket cleanup');
        (window as any).__finderwise_cleanup();
      }

      this.logger.info('AppLifecycleService', 'App closing gracefully');
    });
  }


  /**
   * Get current state (for debugging)
   */
  public getCurrentState(): string {
    return localStorage.getItem(this.STATE_KEY) || 'unknown';
  }

  /**
   * Get time since last heartbeat (for debugging)
   */
  public getTimeSinceLastHeartbeat(): number {
    const lastHeartbeat = localStorage.getItem(this.HEARTBEAT_KEY);
    if (!lastHeartbeat) return -1;
    return Date.now() - parseInt(lastHeartbeat, 10);
  }

  /**
   * Clear lifecycle state (for testing/debugging)
   */
  public clearState(): void {
    localStorage.removeItem(this.STATE_KEY);
    localStorage.removeItem(this.HEARTBEAT_KEY);
    this.logger.info('AppLifecycleService', 'Lifecycle state cleared');
  }
}
